'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { FormLabel, InputSelect } from '~/components/primitives';
import { HoverTooltip } from './HoverTooltip';
import { unpinnedLocationStatement } from '../utils/locationStatement';
import { useI18n } from '~/features/i18n';
import { setWidgetFixedAddress, setWidgetMode } from '../store';
import { addressCityLabel } from '~/features/activities/addressCityLabel';
import { useCustomLocationLabel } from '~/features/activities/useCustomLocationLabel';
import { LOCATION_CUSTOM_VALUE } from '~/features/cart/cartItemLocation';
type Props = {
  /** The activity's fixed locations (partner venues). */
  addresses: ReadonlyArray<{
    id: string | number;
    name?: string | null;
  }>;
  /** Whether "Locatie naar keuze" (partner comes to you) is available. */
  comesToYou: boolean;
  /** The location the booking card will submit; null shows "Kies je locatie". */
  value: typeof LOCATION_CUSTOM_VALUE | number | null;
  className?: string;
};

/**
 * Right-panel location selector, styled to match the Variant/Taal fields in the
 * booking card. "Locatie naar keuze" drives the widget into custom mode (partner
 * comes to me); a fixed location drives it into fixed mode, through the widget store
 * the map reads too. It shows `value`, the location `QuotationCard` will submit.
 */
export const LocationDropdown: React.FC<Props> = t0 => {
  const $ = _c(56);
  const {
    addresses,
    comesToYou,
    value,
    className
  } = t0;
  const i18n = useI18n();
  const customLocationLabel = useCustomLocationLabel();
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== addresses || $[1] !== className || $[2] !== comesToYou || $[3] !== customLocationLabel || $[4] !== i18n || $[5] !== value) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[16] !== i18n) {
        t10 = (address, index) => ({
          value: String(address.id),
          label: addressCityLabel(address.name) ?? address.name ?? i18n._("pdp.location.dropdown.location_n", {
            n: index + 1
          }, {
            message: "Location {n}"
          })
        });
        $[16] = i18n;
        $[17] = t10;
      } else {
        t10 = $[17];
      }
      const addressOptions = addresses.map(t10);
      if (!comesToYou && addresses.length <= 1) {
        let t11;
        if ($[18] !== className) {
          t11 = cx("flex flex-row items-center gap-5", className);
          $[18] = className;
          $[19] = t11;
        } else {
          t11 = $[19];
        }
        let t12;
        if ($[20] !== i18n) {
          t12 = i18n._("pdp.location.dropdown.label", undefined, {
            message: "Location"
          });
          $[20] = i18n;
          $[21] = t12;
        } else {
          t12 = $[21];
        }
        let t13;
        if ($[22] !== t12) {
          t13 = <FormLabel htmlFor="pdp-location-dropdown" size="md" className="w-25">{t12}</FormLabel>;
          $[22] = t12;
          $[23] = t13;
        } else {
          t13 = $[23];
        }
        let t14;
        if ($[24] !== addresses.length || $[25] !== i18n) {
          t14 = addresses.length === 0 ? unpinnedLocationStatement(i18n) : i18n._("pdp.location.dropdown.fixed_tooltip", undefined, {
            message: "This activity takes place at the provider's location."
          });
          $[24] = addresses.length;
          $[25] = i18n;
          $[26] = t14;
        } else {
          t14 = $[26];
        }
        let t15;
        if ($[27] !== i18n) {
          t15 = i18n._("pdp.location.dropdown.placeholder", undefined, {
            message: "Choose your location"
          });
          $[27] = i18n;
          $[28] = t15;
        } else {
          t15 = $[28];
        }
        const t16 = <HoverTooltip as="div" triggerClassName="w-full" content={t14}><InputSelect id="pdp-location-dropdown" className="w-full" compact={true} disabled={true} value={addressOptions[0]?.value ?? ""} placeholder={t15} options={addressOptions} onChange={_temp} /></HoverTooltip>;
        let t17;
        if ($[29] !== t11 || $[30] !== t13 || $[31] !== t16) {
          t17 = <div className={t11}>{t13}{t16}</div>;
          $[29] = t11;
          $[30] = t13;
          $[31] = t16;
          $[32] = t17;
        } else {
          t17 = $[32];
        }
        t9 = t17;
        break bb0;
      }
      if ($[33] !== className) {
        t7 = cx("flex flex-row items-center gap-5", className);
        $[33] = className;
        $[34] = t7;
      } else {
        t7 = $[34];
      }
      let t11;
      if ($[35] !== i18n) {
        t11 = i18n._("pdp.location.dropdown.label", undefined, {
          message: "Location"
        });
        $[35] = i18n;
        $[36] = t11;
      } else {
        t11 = $[36];
      }
      if ($[37] !== t11) {
        t8 = <FormLabel htmlFor="pdp-location-dropdown" size="md" className="w-25">{t11}</FormLabel>;
        $[37] = t11;
        $[38] = t8;
      } else {
        t8 = $[38];
      }
      T0 = InputSelect;
      t1 = "pdp-location-dropdown";
      t2 = "w-full";
      t3 = true;
      t4 = value == null ? "" : String(value);
      if ($[39] !== i18n) {
        t5 = i18n._("pdp.location.dropdown.placeholder", undefined, {
          message: "Choose your location"
        });
        $[39] = i18n;
        $[40] = t5;
      } else {
        t5 = $[40];
      }
      let t12;
      if ($[41] !== comesToYou || $[42] !== customLocationLabel) {
        t12 = comesToYou ? [{
          value: LOCATION_CUSTOM_VALUE,
          label: customLocationLabel
        }] : [];
        $[41] = comesToYou;
        $[42] = customLocationLabel;
        $[43] = t12;
      } else {
        t12 = $[43];
      }
      t6 = [...addressOptions, ...t12];
    }
    $[0] = addresses;
    $[1] = className;
    $[2] = comesToYou;
    $[3] = customLocationLabel;
    $[4] = i18n;
    $[5] = value;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[44] !== T0 || $[45] !== t1 || $[46] !== t2 || $[47] !== t3 || $[48] !== t4 || $[49] !== t5 || $[50] !== t6) {
    t10 = <T0 id={t1} className={t2} compact={t3} value={t4} placeholder={t5} options={t6} onChange={_temp2} />;
    $[44] = T0;
    $[45] = t1;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
    $[49] = t5;
    $[50] = t6;
    $[51] = t10;
  } else {
    t10 = $[51];
  }
  let t11;
  if ($[52] !== t10 || $[53] !== t7 || $[54] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[52] = t10;
    $[53] = t7;
    $[54] = t8;
    $[55] = t11;
  } else {
    t11 = $[55];
  }
  return t11;
};
function _temp() {}
function _temp2(next) {
  if (next === LOCATION_CUSTOM_VALUE) {
    setWidgetMode("custom");
    return;
  }
  setWidgetFixedAddress(String(next));
}